import type { GarmentState } from '../generated/enums';

/**
 * Which fulfilment moves are legal from where, mirroring `BookingService.IsLegalGarmentMove`.
 *
 * <p>
 * <strong>`Issued` and `Returned` are absent from every list on purpose.</strong> The server refuses
 * them on the per-garment endpoint — those two move stock and money and each has its own counter
 * screen taking a list of garments, so there is exactly one code path that hands a garment over and
 * one that takes it back. Offering them here would be offering a button that exists only to be
 * rejected.
 * </p>
 *
 * <p>
 * <strong>`Replaced` and `Removed` are absent for the same reason</strong>, and `Replaced` had to be
 * taken *out* when amendment landed. It had been offered from `Issued` since the rebuild, against a
 * server that refused it — nothing could create the replacement row, reserve the incoming garment or
 * release the outgoing one, so the button was a 400 waiting to happen. Both now belong to
 * `POST /booking/{id}/amend`, which is the only path that does all three atomically.
 * </p>
 *
 * <p>
 * <strong>Duplicated from the server rather than fetched, and the cost is worth naming:</strong> a
 * transition added in C# and not here is a move the screen quietly stops offering. The alternative —
 * rendering every state and letting the ones that are wrong come back as 400s — teaches a clerk to
 * distrust the screen, which is the more expensive failure.
 * </p>
 *
 * <p>
 * <strong>It moved into the core on 2026-10-14 when the phone became the second caller</strong>
 * (Tomorrow's tick-ready list). The note that stood here said a second caller should take the
 * table off the server as data instead; sharing one client copy was chosen as the smaller step,
 * because it keeps both clients from drifting apart. The server's copy is still a separate one, so
 * the cost named above still applies, now once rather than twice.
 * </p>
 *
 * <p>
 * What is deliberately *not* encoded is whether a move is a good idea — only whether it is
 * structurally allowed. §9's inspection rules and §11's alteration workflow own that, and neither
 * exists yet.
 * </p>
 */
const legalMoves: Partial<Record<GarmentState, readonly GarmentState[]>> = {
  Pending: ['Allocated'],
  Allocated: ['Fitting', 'Alteration', 'Ready'],
  Fitting: ['Alteration', 'Ready', 'Allocated'],
  Alteration: ['Ready', 'Fitting'],
  Ready: ['Alteration', 'Allocated'],
  Returned: ['Inspected'],
  Issued: ['NotReturned'],
};

/** Where this garment can go next. Empty for a terminal state, and for the two counter moves. */
export function movesFor(state: GarmentState): readonly GarmentState[] {
  return legalMoves[state] ?? [];
}

/**
 * The tick beside a garment on the day-before list: whether it reads as ready, and the move a tap
 * sends. Null where there is nothing to tick: a garment not yet set aside, one already out, or one
 * that is finished.
 *
 * <p>
 * <strong>Ticking is the move to `Ready`; unticking goes back to `Allocated`</strong>, the
 * server's own un-ready move, for the garment found marked on the morning of collection. A garment
 * at the tailor is tickable too: ticking it says it came back finished, which closes the booking's
 * own alteration. Both directions come from {@link movesFor}, so a tick the server would refuse is
 * never offered.
 * </p>
 */
export function readyTick(state: GarmentState): { ticked: boolean; next: GarmentState } | null {
  if (state === 'Ready') {
    return movesFor('Ready').includes('Allocated') ? { ticked: true, next: 'Allocated' } : null;
  }

  return movesFor(state).includes('Ready') ? { ticked: false, next: 'Ready' } : null;
}
